import { makeAutoObservable, runInAction } from 'mobx';
import { Project } from '../shared/models/Project';
import { ProjectProgress } from '../shared/models/ProjectProgress';
import { Donation } from '../shared/models/Donation';
import { DonationTeam } from '../shared/models/DonationTeam';
import { ProjectService } from '../services/ProjectService';
import { ProjectProgressService } from '../services/ProjectProgressService';
import { DonationService } from '../services/DonationService';
import { DonationTeamService } from '../services/DonationTeamService';
import * as WechatAuthService from '../services/WechatAuthService';

/**
 * 项目详情 ViewModel（H5 用户端）
 */
export class ProjectDetailViewModel {
  /** 项目信息 */
  project: Project | null = null;

  /** 项目进展列表 */
  progresses: ProjectProgress[] = [];

  /** 项目进展是否还有更多 */
  progressHasMore = false;

  /** 项目进展当前页码 */
  progressPage = 1;

  /** 项目进展每页数量 */
  progressLimit = 10;

  /** 捐赠列表 */
  donations: Donation[] = [];

  /** 捐赠是否还有更多 */
  donationHasMore = false;

  /** 捐赠当前页码 */
  donationPage = 1;

  /** 捐赠每页数量 */
  donationLimit = 10;

  /** 一起捐团队列表（公域） */
  donationTeams: DonationTeam[] = [];
  donationTeamsHasMore = false;
  donationTeamsPage = 1;
  donationTeamsLimit = 5;
  donationTeamsLoading = false;

  /** 我的快捷入口（项目内） */
  myInitiatedTeams: DonationTeam[] = [];
  myJoinedTeams: DonationTeam[] = [];

  /** 加载状态 */
  loading = false;

  /** 项目进展加载状态 */
  progressLoading = false;

  /** 捐赠加载状态 */
  donationLoading = false;

  /** 错误信息 */
  error: string | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * 加载项目详情
   */
  async load(projectId: string) {
    this.loading = true;
    this.error = null;

    try {
      const project = await ProjectService.getProjectById(projectId);
      runInAction(() => {
        this.project = project;
        this.loading = false;
      });

      // 加载项目进展和捐赠列表
      await Promise.all([
        this.loadProgresses(projectId),
        this.loadDonations(projectId),
        this.loadDonationTeams(projectId),
        this.loadMyTeamQuickEntry(projectId),
      ]);
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载项目详情失败';
        this.loading = false;
      });
    }
  }

  /**
   * 加载项目进展
   */
  async loadProgresses(projectId: string, page: number = 1) {
    this.progressLoading = true;
    try {
      const response = await ProjectProgressService.getProjectProgresses({
        projectId,
        page,
        limit: this.progressLimit,
      });

      runInAction(() => {
        if (page === 1) {
          this.progresses = response.list;
        } else {
          this.progresses = [...this.progresses, ...response.list];
        }
        this.progressPage = response.page;
        this.progressHasMore = response.page < response.totalPages;
        this.progressLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载项目进展失败';
        this.progressLoading = false;
      });
    }
  }

  /**
   * 加载更多项目进展
   */
  async loadMoreProgresses() {
    if (!this.project || this.progressLoading || !this.progressHasMore) {
      return;
    }
    await this.loadProgresses(this.project.id, this.progressPage + 1);
  }

  /**
   * 加载捐赠列表
   */
  async loadDonations(projectId: string, page: number = 1) {
    this.donationLoading = true;
    try {
      const response = await DonationService.getDonations({
        projectId,
        page,
        limit: this.donationLimit,
        status: 'PAID', // 只显示已支付的捐赠
      });

      runInAction(() => {
        if (page === 1) {
          this.donations = response.list;
        } else {
          this.donations = [...this.donations, ...response.list];
        }
        this.donationPage = response.page;
        this.donationHasMore = response.page < response.totalPages;
        this.donationLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载捐赠列表失败';
        this.donationLoading = false;
      });
    }
  }

  /**
   * 加载更多捐赠
   */
  async loadMoreDonations() {
    if (!this.project || this.donationLoading || !this.donationHasMore) {
      return;
    }
    await this.loadDonations(this.project.id, this.donationPage + 1);
  }

  async loadDonationTeams(projectId: string, page: number = 1) {
    this.donationTeamsLoading = true;
    try {
      const openId = WechatAuthService.getOpenId();
      const response = await DonationTeamService.listProjectTeams(projectId, {
        page,
        limit: this.donationTeamsLimit,
        openId: openId || undefined,
      });
      runInAction(() => {
        if (page === 1) {
          this.donationTeams = response.list;
        } else {
          this.donationTeams = [...this.donationTeams, ...response.list];
        }
        this.donationTeamsPage = response.page;
        this.donationTeamsHasMore = response.page < response.totalPages;
        this.donationTeamsLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : '加载一起捐团队失败';
        this.donationTeamsLoading = false;
      });
    }
  }

  async loadMyTeamQuickEntry(projectId: string) {
    const openId = WechatAuthService.getOpenId();
    if (!openId) {
      runInAction(() => {
        this.myInitiatedTeams = [];
        this.myJoinedTeams = [];
      });
      return;
    }
    try {
      const data = await DonationTeamService.getProjectMyQuickTeams(projectId, openId);
      runInAction(() => {
        this.myInitiatedTeams = data.initiated || [];
        this.myJoinedTeams = data.joined || [];
      });
    } catch {
      runInAction(() => {
        this.myInitiatedTeams = [];
        this.myJoinedTeams = [];
      });
    }
  }
}
